import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Minimize2 } from 'lucide-react';
import { posthog } from 'posthog-js';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import DeviceSelection from './deviceSelection';
import RecorderControls from './RecorderControls';
import Notepad from './Notepad';
import { RootState } from '../store';
import PopupRecorder from './PopupRecorder';
import { setUIMode } from '../recorderSlice';
import { Z_INDEX } from '../../constants/zIndex';
import SkipDeviceSelection from './skipDeviceSelection';
import { streams } from './utils/streamUtils';
import {
  AudioLevelDetector,
  AudioLevelDetectorConfig,
} from './utils/audioLevelDetector';
import useUploadPulse from '../hooks/useUploadPulse';
import type { AudioStatus } from './RecorderControls/AudioStatusBadge';

const RecorderComponent = () => {
  const dispatch = useDispatch();
  const { status, controls, settings, selectedDevices } = useSelector(
    (state: RootState) => state.recorder,
  );
  const [showNotes, setShowNotes] = useState(() => {
    const savedValue = localStorage.getItem('notepad-visible');
    return savedValue ? JSON.parse(savedValue) : true;
  });

  // Microphone audio detection state
  const [micAudioWarning, setMicAudioWarning] = useState(false);
  const [micEverDetected, setMicEverDetected] = useState(false);
  const micDetector = useRef<AudioLevelDetector | null>(null);
  const micAnalyser = useRef<AnalyserNode | null>(null);
  const hasMicDetectedRef = useRef<boolean>(false);

  // System/Tab audio detection state
  const [systemAudioWarning, setSystemAudioWarning] = useState(false);
  const [systemEverDetected, setSystemEverDetected] = useState(false);
  const [tabAudioActive, setTabAudioActive] = useState(false);
  const systemDetector = useRef<AudioLevelDetector | null>(null);
  const systemAnalyser = useRef<AnalyserNode | null>(null);
  const hasSystemDetectedRef = useRef<boolean>(false);

  // Shared AudioContext
  const audioContext = useRef<AudioContext | null>(null);

  // Recording analytics tracking
  const recordingStartTime = useRef<number | null>(null);

  const isBackdropInvisible = status === 'stopped';
  const showDeviceSelection = status === 'initializing' || status === 'ready';
  const isSaving = status === 'saving';
  const isRecording = status === 'recording';
  const { uiMode, isMountedFromPlugin } = controls;
  const { webCam, screen } = settings?.videoSettings || {};
  const { microphoneAudio, browserAudio } = settings?.audioSettings || {};
  // Tab audio is only possible when sharing a browser tab (not window/monitor)
  const tabAudioPossible =
    browserAudio && selectedDevices?.displaySurface === 'browser';
  const hasAnyAudioEnabled = microphoneAudio || tabAudioPossible;

  // Microphone audio detection callbacks
  const handleMicSilent = () => {
    console.log('Microphone silent - no audio for 5 seconds');
    if (isRecording && !hasMicDetectedRef.current) {
      setMicAudioWarning(true);
    }
  };

  const handleMicAudioDetected = () => {
    hasMicDetectedRef.current = true;
    setMicEverDetected(true);
    setMicAudioWarning(false);
  };

  // System/Tab audio detection callbacks
  const handleSystemSilent = () => {
    console.log('System/Tab silent - no audio for 5 seconds');
    if (isRecording && !hasSystemDetectedRef.current) {
      setSystemAudioWarning(true);
    }
  };

  const handleSystemAudioDetected = () => {
    hasSystemDetectedRef.current = true;
    setSystemEverDetected(true);
    setSystemAudioWarning(false);
  };

  // Helper function to configure analyser
  const configureAnalyser = (analyserNode: AnalyserNode) => {
    /* eslint-disable no-param-reassign */
    analyserNode.fftSize = 2048;
    analyserNode.minDecibels = -100;
    analyserNode.maxDecibels = -30;
    analyserNode.smoothingTimeConstant = 0.8;
    /* eslint-enable no-param-reassign */
  };

  // Heartbeat interval to keep backend aware of ongoing upload
  useUploadPulse();

  useEffect(() => {
    const isActiveRecording = status === 'recording' || status === 'paused';

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isActiveRecording) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [status]);

  // Audio level detection setup - SEPARATE detectors for mic and system audio
  useEffect(() => {
    const setupAudioLevelDetection = async () => {
      if (!isRecording || !hasAnyAudioEnabled) return;

      try {
        // Reset detection states
        hasMicDetectedRef.current = false;
        hasSystemDetectedRef.current = false;
        setMicAudioWarning(false);
        setSystemAudioWarning(false);
        setMicEverDetected(false);
        setSystemEverDetected(false);
        recordingStartTime.current = Date.now();

        // Create shared AudioContext
        audioContext.current = new (window.AudioContext ||
          (window as any).webkitAudioContext)();

        // Audio detection config
        const audioDetectionConfig: AudioLevelDetectorConfig = {
          rmsThreshold: 0.02,
          rmsDbThreshold: -50,
          voiceEnergyThreshold: 0.1,
          silentDurationThreshold: 5000,
          sampleRate: 200,
        };

        // Setup Microphone detector (if mic enabled)
        if (microphoneAudio) {
          if (streams.userMediaStream?.getAudioTracks().length > 0) {
            micAnalyser.current = audioContext.current.createAnalyser();
            configureAnalyser(micAnalyser.current);

            const micSource = audioContext.current.createMediaStreamSource(
              streams.userMediaStream,
            );
            micSource.connect(micAnalyser.current);

            micDetector.current = new AudioLevelDetector(
              micAnalyser.current,
              audioDetectionConfig,
              handleMicSilent,
              handleMicAudioDetected,
              () => {},
            );
            micDetector.current.start();
            console.log('Microphone audio detection started');
          } else {
            // Mic enabled in settings but no audio tracks in stream
            console.log('Microphone enabled but no audio tracks available');
            setMicAudioWarning(true);
          }
        }

        // Setup System/Tab detector (only when sharing a browser tab with audio)
        if (tabAudioPossible) {
          const hasTabAudioTracks =
            (streams.displayMediaStream?.getAudioTracks().length ?? 0) > 0;
          setTabAudioActive(hasTabAudioTracks);

          if (hasTabAudioTracks) {
            systemAnalyser.current = audioContext.current.createAnalyser();
            configureAnalyser(systemAnalyser.current);

            const systemSource = audioContext.current.createMediaStreamSource(
              streams.displayMediaStream,
            );
            systemSource.connect(systemAnalyser.current);

            systemDetector.current = new AudioLevelDetector(
              systemAnalyser.current,
              audioDetectionConfig,
              handleSystemSilent,
              handleSystemAudioDetected,
              () => {},
            );
            systemDetector.current.start();
            console.log('System/Tab audio detection started');
          }
        } else {
          setTabAudioActive(false);
        }
      } catch (error) {
        console.error('Error setting up audio level detection:', error);
      }
    };

    const cleanupAudioLevelDetection = () => {
      // Track analytics before cleanup
      if (recordingStartTime.current && (microphoneAudio || tabAudioPossible)) {
        const recordingDuration = Date.now() - recordingStartTime.current;
        const recordingDurationSeconds = recordingDuration / 1000;

        // Track if recording was > 20 seconds and no audio detected from either source
        if (
          recordingDurationSeconds > 20 &&
          !hasMicDetectedRef.current &&
          !hasSystemDetectedRef.current
        ) {
          posthog.capture('recording-no-audio', {
            recordingDurationSeconds: Math.round(recordingDurationSeconds),
            recordingDurationMs: recordingDuration,
            micDetected: false,
            systemDetected: false,
            microphoneEnabled: !!microphoneAudio,
            browserAudioEnabled: !!tabAudioPossible,
            timestamp: new Date().toISOString(),
          });
          console.log(
            `PostHog: Tracked recording with no audio detected (${recordingDurationSeconds}s)`,
          );
        }
      }

      // Stop both detectors
      micDetector.current?.stop();
      systemDetector.current?.stop();
      micDetector.current = null;
      systemDetector.current = null;

      // Close shared audio context
      if (audioContext.current?.state !== 'closed') {
        audioContext.current?.close();
      }
      audioContext.current = null;
      micAnalyser.current = null;
      systemAnalyser.current = null;

      // Reset all states
      hasMicDetectedRef.current = false;
      hasSystemDetectedRef.current = false;
      setMicAudioWarning(false);
      setSystemAudioWarning(false);
      setMicEverDetected(false);
      setSystemEverDetected(false);
      setTabAudioActive(false);
      recordingStartTime.current = null;
    };

    if (isRecording && (microphoneAudio || tabAudioPossible)) {
      setupAudioLevelDetection();
    } else {
      cleanupAudioLevelDetection();
    }

    return cleanupAudioLevelDetection;
  }, [isRecording, microphoneAudio, tabAudioPossible]);

  // Compute overall audio detection status for inline badge
  const audioStatus: AudioStatus | null = useMemo(() => {
    if (!isRecording) return null;

    const micEnabled = !!microphoneAudio;
    // Tab audio was requested (browserAudio + sharing a browser tab)
    const tabRequested = !!tabAudioPossible;
    // Tab audio has actual audio tracks (user checked "Share tab audio")
    const tabEnabled = tabRequested && tabAudioActive;
    // If tab audio was requested but no tracks available, it's a failure
    const tabFailedToShare = tabRequested && !tabAudioActive;

    // Nothing enabled at all — no badge
    if (!micEnabled && !tabRequested) return null;

    // If tab audio was requested but user didn't share it, show error immediately
    if (tabFailedToShare && !micEnabled) return 'no-audio';
    if (tabFailedToShare && micEnabled) return 'only-microphone';

    // Check if each source's detection phase has resolved
    const micResolved = !micEnabled || micEverDetected || micAudioWarning;
    const tabResolved = !tabEnabled || systemEverDetected || systemAudioWarning;

    // Still initializing if neither source has resolved
    if (!micResolved && !tabResolved) return 'initializing';
    // If one enabled source hasn't resolved yet, still initializing
    if (!micResolved || !tabResolved) return 'initializing';

    const micOk = !micEnabled || micEverDetected;
    const tabOk = !tabEnabled || systemEverDetected;

    if (micOk && tabOk) return 'connected';
    // Only show partial statuses when the source is actually enabled
    if (micEnabled && micOk && !tabOk) return 'only-microphone';
    if (tabEnabled && !micOk && tabOk) return 'only-tab';
    return 'no-audio';
  }, [
    isRecording,
    microphoneAudio,
    tabAudioPossible,
    tabAudioActive,
    micEverDetected,
    systemEverDetected,
    micAudioWarning,
    systemAudioWarning,
  ]);

  const handleToggleNotes = () => {
    setShowNotes((prev: boolean) => {
      const newValue = !prev;
      localStorage.setItem('notepad-visible', JSON.stringify(newValue));
      return newValue;
    });
  };

  const handleSwitchToPopup = () => {
    dispatch(setUIMode('popup'));
  };

  if (showDeviceSelection) {
    // If no screen or webcam is selected, record audio will start instantly
    if (!webCam && !screen) {
      return <SkipDeviceSelection />;
    }
    return <DeviceSelection />;
  }

  // Use the popup UI mode if selected
  if (uiMode === 'popup' && !showDeviceSelection) {
    return <PopupRecorder />;
  }

  return (
    <div
      className={cn(
        'fixed inset-0 flex items-start justify-center bg-background',
        isBackdropInvisible && !isSaving && 'invisible',
      )}
      style={{ zIndex: Z_INDEX.popover }}
    >
      <div
        className={cn(
          'relative flex flex-col w-full max-w-[600px] h-full p-2 transition-opacity',
          isBackdropInvisible && !isSaving ? 'opacity-0' : 'opacity-100',
        )}
      >
        {/* Minimize button - only shown when mounted from plugin */}
        {isMountedFromPlugin && (
          <div className="absolute top-2.5 right-2.5 z-10">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSwitchToPopup}
              className="h-8 w-8 p-0 bg-black/10 hover:bg-black/20"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
          </div>
        )}

        <RecorderControls
          onToggleNotes={handleToggleNotes}
          showNotes={showNotes}
          audioStatus={audioStatus}
        />

        <div
          className={cn(
            'w-full flex overflow-hidden transition-all duration-200',
            showNotes ? 'flex-1' : 'flex-none',
          )}
        >
          {showNotes && (
            <div className="flex-1 min-h-0">
              <Notepad />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecorderComponent;
